import {Component, ElementRef, EventEmitter, inject, Input, Output, signal, viewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {catchError} from 'rxjs/operators';
import {of, take, tap} from 'rxjs';
import {HttpStatusCode} from '@angular/common/http';
import {TranslocoDirective} from '@jsverse/transloco';
import {LogInIcon, LucideAngularModule} from 'lucide-angular';
import {RouteGenerator} from '@core/route-generator';
import {User} from '@domain/user/user';
import {UI_ROUTES} from '@core/ui.routes';
import {AuthService} from '@core/auth/auth.service';
import {LoginRequest} from '@core/auth/auth.interfaces';
import {EmailFieldComponent} from '@shared/form/string/email-field/email-field.component';
import {PasswordFieldComponent} from '@shared/form/string/password-field/password-field.component';
import {AltchaComponent} from '@shared/altcha.component';

/**
 * Inspired by:
 * - https://dev-academy.com/angular-user-login-and-registration-guide-cookies-and-jwt/
 * - https://github.com/bartosz-io/budget-angular/tree/master/src/app/auth
 */
@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoDirective,
    EmailFieldComponent,
    LucideAngularModule,
    EmailFieldComponent,
    PasswordFieldComponent,
    AltchaComponent
  ],
  templateUrl: './login.component.html'
})
export class LoginComponent {

  @Input()
  canRegister = true;
  @Input()
  loginBtnLabel: string;
  @Input()
  redirect = true;

  @Output() onLogin = new EventEmitter<User>();

  public readonly resetPasswordDialog = viewChild.required<ElementRef<HTMLDialogElement>>('resetPasswordDialog');

  private readonly formBuilder = inject(FormBuilder);

  public readonly registerRoute = RouteGenerator.abs(UI_ROUTES.auth.register);

  public readonly icons = {
    login: LogInIcon
  };

  public readonly loginForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required]
  });

  public readonly resetForm = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    captcha: ['', Validators.required]
  });
  public isBadCredentials = false;
  public submitted = signal(false);

  public constructor(private readonly authService: AuthService) {}

  public login(): void {
    this.submitted.set(true);
    if (!this.loginForm.valid) {
      return;
    }
    const loginRequest: LoginRequest = {
      username: this.loginForm.controls.email?.value || '',
      password: this.loginForm.controls.password?.value || '',
    };

    this.authService.login(
      loginRequest,
      {
        noRedirect: !this.redirect
      }
    ).pipe(
      take(1),
      catchError(err => {
        this.isBadCredentials = err?.status === HttpStatusCode.Unauthorized;
        return of(null);
      }),
      tap(user => {
        if (user) {
          this.isBadCredentials = false;
          this.onLogin.emit(user);
        }
      })
    ).subscribe(_user => {});
  }

  public openResetDialog(): void {
    this.resetForm.controls.email.patchValue(this.loginForm.get('email')?.value ?? '');
    this.resetPasswordDialog().nativeElement.showModal();
  }

  public closeResetDialog(): void {
    this.resetPasswordDialog().nativeElement.close();
  }

  public resetPassword(): void {
    const result = this.resetForm.value;
    if (result?.email && result?.captcha) {
      this.authService.sendResetPasswordEmail({
        username: result.email,
        captcha: result.captcha
      }).pipe(
        take(1)
      ).subscribe(_resetEmailSent => {
        this.closeResetDialog();
      });
    }
  }
}
